import React from 'react';

import { graphql, useStaticQuery } from 'gatsby';
import { ALL_LANGS, EXPERIENCES_PREFIX_URL } from '../../constants';
import { ButtonLinkType } from '../../graphql-fragments/ButtonLink';
import i18n from '../../translation/i18n';
import { getUrlFromVersatileLink, localize } from '../../utils/sanity';
import { clsx, urlJoin } from '../../utils/utils';
import Button from './Button';
import * as styles from './ButtonLink.module.scss';
import SmartLink from './SmartLink';

interface ButtonLinkBaseProps extends Omit<React.HTMLAttributes<HTMLAnchorElement>, 'onClick'> {
  outlined?: boolean;
  secondary?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  buttonClassName?: string;
}

export type ButtonLinkProps = ButtonLinkBaseProps &
  (
    | {
        button: ButtonLinkType;
        href?: never;
        children?: never;
      }
    | {
        button?: never;
        href: string;
        children: React.ReactNode;
      }
  );

interface LocalizedQueryData {
  sanityWebsite: {
    browseExperiencesButtonText: string;
  };
}

export default React.forwardRef<any, ButtonLinkProps>(function ButtonLink(
  {
    button,
    href,
    onClick,
    outlined,
    secondary,
    className,
    buttonClassName,
    children,
    ...restProps
  },
  ref,
): React.ReactElement {
  const staticData = useStaticQuery<LocalizedQueryData>(graphql`
    {
      sanityWebsite {
        browseExperiencesButtonText {
          ...LocaleStringFragment
        }
      }
    }
  `);

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: LocalizedQueryData = localize(staticData, langs);

  const { browseExperiencesButtonText } = localizedData.sanityWebsite;

  return button?.linkToExperiencesPage ? (
    <SmartLink
      ref={ref}
      className={clsx(className, styles.link)}
      to={urlJoin(EXPERIENCES_PREFIX_URL)}
      tabIndex={-1}
      {...restProps}
    >
      <Button
        onClick={onClick}
        outlined={outlined}
        className={buttonClassName}
        secondary={secondary}
      >
        {browseExperiencesButtonText}
      </Button>
    </SmartLink>
  ) : (
    <SmartLink
      ref={ref}
      className={clsx(className, styles.link)}
      to={button ? getUrlFromVersatileLink(button) : href}
      tabIndex={-1}
      {...restProps}
    >
      <Button
        onClick={onClick}
        outlined={outlined}
        className={buttonClassName}
        secondary={secondary}
      >
        {button?.text || children}
      </Button>
    </SmartLink>
  );
});
